import React, { useState, useEffect, useCallback } from "react";
import PropTypes from "prop-types";
import {
  Box,
  TextField,
  InputAdornment,
  Card,
  CardContent,
  CardHeader,
  Typography,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import CustomDialogBoxV2 from "../../common/customDialogBox/CustomDialogBoxV2";
import CustomPaginationV2 from "../../common/pagination/CustomPaginationV2";
import VlcCardHeader from "../cards/VlcCardHeader";
import { vlcComponentStyle, vlcCardStyles } from "../config/vlcConfig";
import { HandleApiActions, labCar } from "../../../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import { componentStyle } from "../../../../config/appTheme";
import VlcCardContent from "../cards/VlcCardContent";
import CustomToolTip from "../../../infoButton/CustomToolTip";
import CustomRadioButtonV2 from "../../common/radioButton/CustomRadioButtonV2";
import CustomSelectV2 from "../../common/select/CustomSelectV2";

const getSearchConfig = ({
  isTransferringVC,
  isTransferringEE,
  isTransferringSWM,
  selectedZone,
  selectedVS,
  searchTerm,
  isZonal,
}) => {
  const trimmed = searchTerm.trim();
  if (isTransferringVC) {
    return {
      apiAction: labCar.get_vehicle_systems_all,
      params: {
        ...(isZonal && selectedZone && { zone_position: selectedZone }),
        sort_field: "created_at",
        sort_order: "ASC",
        ...(trimmed ? { name: trimmed } : {}),
      },
    };
  }
  if (isTransferringEE) {
    return {
      apiAction: labCar.get_vehicle_components_all,
      params: {
        ...(selectedVS && { vs_id: selectedVS }),
        sort_field: "created_at",
        sort_order: "ASC",
        ...(trimmed ? { name: trimmed } : {}),
      },
    };
  }
  if (isTransferringSWM) {
    return {
      apiAction: labCar.get_ee_components_new,
      params: {
        ...(isZonal && selectedZone && { zone_position: selectedZone }),
        ...(trimmed ? { assembly_number: trimmed } : {}),
      },
    };
  }
  return {
    apiAction: labCar.get_ee_components_new,
    params: {
      ...(trimmed ? { assembly_number: trimmed } : {}),
    },
  };
};

const buildVsTransferConfig = (base, selectedArchivedVSs, selectedParent) => {
  if (!selectedArchivedVSs?.length) return null;
  const vsZoneDetails = selectedParent?.zone_name
    ? {
        position: selectedParent.position,
        zone_name: selectedParent.zone_name,
      }
    : selectedParent?.position?.toString();

  return {
    apiAction: labCar.vs_transfer_zone,
    payload: {
      ...base,
      zone_details: vsZoneDetails,
      vs_id: selectedArchivedVSs,
    },
  };
};

const buildVcTransferConfig = (base, selectedArchivedVCs, targetParentId) => {
  if (!selectedArchivedVCs?.length) return null;
  return {
    apiAction: labCar.vc_transfer_zone,
    payload: {
      ...base,
      vs_id: targetParentId,
      vc_id: selectedArchivedVCs,
    },
  };
};

const resolveEeZoneDetails = ({
  isZone,
  selectedParent,
  targetParentId,
  isZonal,
  selectedZone,
  selectedProjectDetails,
}) => {
  if (isZone) {
    if (selectedParent?.zone_name) {
      return {
        position: selectedParent.position,
        zone_name: selectedParent.zone_name,
      };
    }
    if (targetParentId != null) {
      return {
        position: targetParentId,
        zone_name: selectedParent?.zone_name || "",
      };
    }
    return undefined;
  }

  if (isZonal && selectedZone != null && selectedZone !== "") {
    const matchedZone = selectedProjectDetails?.project_zones?.find(
      (z) =>
        String(z.position) === String(selectedZone) ||
        z.zone_name === selectedZone
    );
    return {
      position: matchedZone?.position ?? selectedZone,
      zone_name: matchedZone?.zone_name || "",
    };
  }

  return undefined;
};

const buildEeTransferConfig = ({
  base,
  selectedArchivedEEs,
  eeTransferMode,
  selectedParent,
  targetParentId,
  isZonal,
  selectedZone,
  selectedProjectDetails,
}) => {
  if (!selectedArchivedEEs?.length) return null;
  const isZone = eeTransferMode === "ZONE";
  const zoneDetailsVal = resolveEeZoneDetails({
    isZone,
    selectedParent,
    targetParentId,
    isZonal,
    selectedZone,
    selectedProjectDetails,
  });

  return {
    apiAction: labCar.ee_transfer_zone,
    payload: {
      ...base,
      ee_id: selectedArchivedEEs,
      ...(zoneDetailsVal && { zone_details: zoneDetailsVal }),
      ...(!isZone && { vc_id: targetParentId }),
    },
  };
};

const buildSwmTransferConfig = (base, selectedArchivedSWMs, targetParentId) => {
  if (!selectedArchivedSWMs?.length) return null;
  return {
    apiAction: labCar.transfer_zone,
    payload: {
      ...base,
      ee_id: targetParentId,
      swm_id: selectedArchivedSWMs,
    },
  };
};

const getTransferPayloadAndAction = ({
  isTransferringVS,
  isTransferringVC,
  isTransferringEE,
  targetParentId,
  selectedArchivedVSs,
  selectedArchivedVCs,
  selectedArchivedEEs,
  selectedArchivedSWMs,
  eeTransferMode,
  selectedParent,
  projectId,
  variantId,
  isZonal,
  selectedZone,
  selectedProjectDetails,
}) => {
  const base = {
    project_id: projectId,
    variant_id: variantId,
  };

  if (isTransferringVS) {
    return buildVsTransferConfig(base, selectedArchivedVSs, selectedParent);
  }

  if (isTransferringVC) {
    return buildVcTransferConfig(base, selectedArchivedVCs, targetParentId);
  }

  if (isTransferringEE) {
    return buildEeTransferConfig({
      base,
      selectedArchivedEEs,
      eeTransferMode,
      selectedParent,
      targetParentId,
      isZonal,
      selectedZone,
      selectedProjectDetails,
    });
  }

  return buildSwmTransferConfig(base, selectedArchivedSWMs, targetParentId);
};

const parseSearchResponse = (response) => {
  if (!response?.data) {
    return { dataToSet: [], total: 0 };
  }
  let dataToSet = [];
  if (Array.isArray(response.data)) {
    dataToSet = response.data;
  } else if (response.data && Array.isArray(response.data.data)) {
    dataToSet = response.data.data;
  }
  const total =
    response.totalElements ||
    response.total_elements ||
    response.total_items ||
    response.totalCount ||
    response.total_count ||
    0;
  return { dataToSet, total };
};

function TransferDestinationDialog({ open, onClose, value, onChange, onNext }) {
  return (
    <CustomDialogBoxV2
      open={open}
      onClose={onClose}
      title="Change Parent"
      dialogRadius="0rem"
      requiresDefaultPaddingForContent={false}
      headerSlot={<></>}
      successButtonName="Next"
      rejectButtonName="Cancel"
      actionButtonStyle="continuous"
      onClickSuccess={onNext}
      onClickReject={onClose}
    >
      <Box sx={{ width: "400px", maxWidth: "100%", p: 3 }}>
        <Typography variant="body1" sx={{ mb: 2 }}>
          Select transfer destination:
        </Typography>
        <CustomRadioButtonV2
          data={[
            { value: "ZONE", label: "Zone" },
            { value: "VC", label: "Vehicle Component" },
          ]}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </Box>
    </CustomDialogBoxV2>
  );
}

TransferDestinationDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
};

const getSearchPlaceholder = ({ isTransferringVS, isTransferringVC, isTransferringEE, eeTransferMode }) => {
  if (isTransferringVS || (isTransferringEE && eeTransferMode === "ZONE")) {
    return "Search Zone Name";
  }
  if (isTransferringVC) {
    return "Search Vehicle System";
  }
  if (isTransferringEE && eeTransferMode === "VC") {
    return "Search Vehicle Component";
  }
  return "Search Assembly Number";
};

const getItemNoLabel = (item, isZoneMode, isTransferringVC, isTransferringEE, eeTransferMode) => {
  if (isZoneMode) return item?.zone_name ?? "";
  if (isTransferringVC) return `Vehicle System: ${item?.name ?? ""}`;
  if (isTransferringEE && eeTransferMode === "VC") {
    return `Vehicle Component: ${item?.name ?? item?.assembly_number ?? ""}`;
  }
  return `Assembly No: ${item?.assembly_number ?? ""}`;
};

const getItemSub1Label = (item, isZoneMode, isVCMode) => {
  if (isZoneMode) return null;
  return isVCMode ? `Name: ${item?.name ?? ""}` : `Version: ${item?.version ?? ""}`;
};

const getItemId = (item, isZoneMode, isVCMode) => {
  if (isZoneMode) return item?.position;
  if (isVCMode) return item?.id;
  return item?.ee_id;
};

const getItemLabels = ({ item, isZoneMode, isTransferringVC, isTransferringEE, eeTransferMode, isVCMode }) => {
  const itemId = getItemId(item, isZoneMode, isVCMode);
  const itemNoLabel = getItemNoLabel(item, isZoneMode, isTransferringVC, isTransferringEE, eeTransferMode);
  const itemSub1Label = getItemSub1Label(item, isZoneMode, isVCMode);
  const itemSub2Label = isZoneMode ? null : `Description: ${item?.description ?? ""}`;

  return { itemId, itemNoLabel, itemSub1Label, itemSub2Label };
};

function ChangeParentItemCard({
  item,
  isZoneMode,
  isVCMode,
  isTransferringVC,
  isTransferringEE,
  eeTransferMode,
  selectedParent,
  selectedParentEE,
  onSelectParent,
  onSelectParentEE,
}) {
  const { itemId, itemNoLabel, itemSub1Label, itemSub2Label } = getItemLabels({
    item,
    isZoneMode,
    isTransferringVC,
    isTransferringEE,
    eeTransferMode,
    isVCMode,
  });

  const selectedCurrentId = getItemId(selectedParent, isZoneMode, isVCMode);
  const isSelected = selectedCurrentId === itemId;

  let radioValue = "";
  if (isZoneMode) {
    if (selectedParent?.position === itemId) {
      radioValue = itemId;
    }
  } else if (selectedParentEE.includes(itemId)) {
    radioValue = itemId;
  }

  const showRadio = isZoneMode || item?.archive_status?.toUpperCase() === "ARCHIVED";

  return (
    <VlcCardContent
      onClick={() => onSelectParent(item)}
      data={item}
      key={itemId}
      isSelected={isSelected}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "stretch",
          cursor: "pointer",
        }}
      >
        {showRadio && (
          <Box
            sx={{
              ml: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <CustomRadioButtonV2
              noPaddingRequired={true}
              data={[{ value: itemId, label: "" }]}
              value={radioValue}
              onChange={() => {
                if (isZoneMode) {
                  onSelectParent(item);
                } else {
                  onSelectParentEE([itemId]);
                }
              }}
            />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              p: 1,
            }}
          >
            <Typography
              sx={{
                ...componentStyle.font.text,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
            >
              <CustomToolTip
                type="string"
                length={50}
                style={{
                  ...componentStyle.font.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                  fontWeight: isZoneMode ? "normal" : "bold",
                }}
                title={itemNoLabel}
              >
                {itemNoLabel}
              </CustomToolTip>
            </Typography>

            {itemSub1Label && (
              <Typography
                sx={{
                  ...componentStyle.font.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                  color: vlcCardStyles.vlcCardListColor,
                }}
              >
                <CustomToolTip
                  type="string"
                  length={60}
                  style={{
                    ...componentStyle.font.text,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  }}
                  title={itemSub1Label}
                >
                  {itemSub1Label}
                </CustomToolTip>
              </Typography>
            )}

            {itemSub2Label && (
              <Typography
                sx={{
                  ...componentStyle.font.text,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: "100%",
                  color: vlcCardStyles.vlcCardListColor,
                }}
              >
                <CustomToolTip
                  type="string"
                  length={60}
                  style={{
                    ...componentStyle.font.text,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: "100%",
                  }}
                  title={itemSub2Label}
                >
                  {itemSub2Label}
                </CustomToolTip>
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </VlcCardContent>
  );
}

ChangeParentItemCard.propTypes = {
  item: PropTypes.object.isRequired,
  isZoneMode: PropTypes.bool,
  isVCMode: PropTypes.bool,
  isTransferringVC: PropTypes.bool,
  isTransferringEE: PropTypes.bool,
  eeTransferMode: PropTypes.string,
  selectedParent: PropTypes.object,
  selectedParentEE: PropTypes.array,
  onSelectParent: PropTypes.func.isRequired,
  onSelectParentEE: PropTypes.func.isRequired,
};

function ChangeParentSearchHeader({
  showZoneSelect,
  showVSSelect,
  selectedProjectDetails,
  selectedZone,
  setSelectedZone,
  selectedVS,
  setSelectedVS,
  vehicleSystems,
  setPage,
  isTransferringVS,
  isTransferringVC,
  isTransferringEE,
  eeTransferMode,
  assemblySearch,
  setAssemblySearch,
}) {
  return (
    <VlcCardHeader
      title="Select parent component"
      showRecreateIcon={false}
    >
      {showZoneSelect && (
        <Box sx={{ mt: 2, display: "flex", gap: 2 }}>
          <Box sx={{ flex: 1 }}>
            <CustomSelectV2
              label="Select Zone"
              optionsList={
                selectedProjectDetails?.project_zones?.map((z) => ({
                  label: z.zone_name,
                  value: z.position,
                })) || []
              }
              selectedValue={selectedZone}
              handleOnChange={(e) => {
                setSelectedZone(e.target.value);
                setSelectedVS("");
                setPage(1);
              }}
            />
          </Box>
          {showVSSelect && (
            <Box sx={{ flex: 1 }}>
              <CustomSelectV2
                label="Select Vehicle System"
                optionsList={
                  vehicleSystems?.map((vs) => ({
                    label: vs.name,
                    value: vs.id,
                  })) || []
                }
                selectedValue={selectedVS}
                handleOnChange={(e) => {
                  setSelectedVS(e.target.value);
                  setPage(1);
                }}
              />
            </Box>
          )}
        </Box>
      )}
      <TextField
        size="small"
        placeholder={getSearchPlaceholder({
          isTransferringVS,
          isTransferringVC,
          isTransferringEE,
          eeTransferMode,
        })}
        variant="outlined"
        fullWidth
        sx={{ ...componentStyle?.searchField?.textField, mt: 2 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search fontSize="small" />
            </InputAdornment>
          ),
        }}
        value={assemblySearch}
        onChange={(e) => setAssemblySearch(e.target.value)}
      />
    </VlcCardHeader>
  );
}

ChangeParentSearchHeader.propTypes = {
  showZoneSelect: PropTypes.bool,
  showVSSelect: PropTypes.bool,
  selectedProjectDetails: PropTypes.object,
  selectedZone: PropTypes.string,
  setSelectedZone: PropTypes.func.isRequired,
  selectedVS: PropTypes.string,
  setSelectedVS: PropTypes.func.isRequired,
  vehicleSystems: PropTypes.array,
  setPage: PropTypes.func.isRequired,
  isTransferringVS: PropTypes.bool,
  isTransferringVC: PropTypes.bool,
  isTransferringEE: PropTypes.bool,
  eeTransferMode: PropTypes.string,
  assemblySearch: PropTypes.string,
  setAssemblySearch: PropTypes.func.isRequired,
};

function useChangeParentSearch({
  open,
  selectedProjectDetails,
  selectedVariantDetails,
  isZoneMode,
  isTransferringVC,
  isTransferringEE,
  isTransferringSWM,
  eeTransferMode,
  selectedZone,
  selectedVS,
  page,
  rowsPerPage,
  assemblySearch,
  dispatch,
}) {
  const [searchResults, setSearchResults] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  const handleSearch = useCallback(async (searchTerm, pageNumber) => {
    if (isZoneMode) {
      let filteredZones = selectedProjectDetails?.project_zones || [];
      if (searchTerm.trim()) {
        const lowerSearch = searchTerm.toLowerCase();
        filteredZones = filteredZones.filter((z) => z.zone_name?.toLowerCase().includes(lowerSearch));
      }
      setSearchResults(filteredZones);
      setTotalItems(filteredZones.length);
      return;
    }

    if (isTransferringSWM && selectedProjectDetails?.is_zonal && !selectedZone) {
      setSearchResults([]);
      setTotalItems(0);
      return;
    }

    const isZonal = Boolean(selectedProjectDetails?.is_zonal);
    const { apiAction, params } = getSearchConfig({
      isTransferringVC,
      isTransferringEE,
      isTransferringSWM,
      selectedZone,
      selectedVS,
      searchTerm,
      isZonal,
    });

    const reqObj = {
      project_id: selectedProjectDetails?.project_id,
      variant_id: selectedVariantDetails?.variant_id,
      page_number: pageNumber,
      page_size: rowsPerPage,
      archive: false,
      ...params,
    };

    try {
      const response = await Promise.resolve(
        dispatch(HandleApiActions({
          ...apiAction,
          params: reqObj,
        }))
      );
      const { dataToSet, total } = parseSearchResponse(response);
      setSearchResults(dataToSet);
      setTotalItems(total);
    } catch (err) {
      console.error("Error fetching change parent search results:", err);
      setSearchResults([]);
      setTotalItems(0);
    }
  }, [
    isZoneMode,
    isTransferringVC,
    isTransferringEE,
    isTransferringSWM,
    selectedZone,
    selectedVS,
    selectedProjectDetails,
    selectedVariantDetails,
    rowsPerPage,
    dispatch,
  ]);

  useEffect(() => {
    if (open) {
      const delayDebounceFn = setTimeout(() => {
        if (!isTransferringEE || eeTransferMode) {
          handleSearch(assemblySearch, page).catch(console.error);
        }
      }, 500);

      return () => clearTimeout(delayDebounceFn);
    }
  }, [assemblySearch, page, selectedZone, selectedVS, open, eeTransferMode, isTransferringEE, handleSearch]);

  return { searchResults, totalItems };
}

function useVehicleSystems({
  eeTransferMode,
  selectedZone,
  selectedProjectDetails,
  selectedVariantDetails,
  dispatch,
}) {
  const [vehicleSystems, setVehicleSystems] = useState([]);
  const [selectedVS, setSelectedVS] = useState("");

  useEffect(() => {
    if (eeTransferMode === "VC" && selectedZone) {
      dispatch(
        HandleApiActions({
          ...labCar.get_vehicle_systems_all,
          params: {
            project_id: selectedProjectDetails?.project_id,
            variant_id: selectedVariantDetails?.variant_id,
            zone_position: selectedZone,
            archive: false,
            page_number: 1,
            page_size: 1000,
          },
        })
      ).then((res) => {
        let vsData = [];
        if (res?.data) {
          vsData = Array.isArray(res.data) ? res.data : (res.data.data || []);
        } else if (res?.data?.data) {
          vsData = Array.isArray(res.data.data) ? res.data.data : [];
        }
        setVehicleSystems(vsData);
        if (vsData.length > 0) {
          setSelectedVS(vsData[0].id);
        } else {
          setSelectedVS("");
        }
      });
    } else {
      setVehicleSystems([]);
      if (eeTransferMode !== "VC") {
        setSelectedVS("");
      }
    }
  }, [eeTransferMode, selectedZone, selectedProjectDetails, selectedVariantDetails, dispatch]);

  return { vehicleSystems, selectedVS, setSelectedVS };
}

const getTargetParentIdHelper = ({ selectedParentEE, isZoneMode, selectedParent, isTransferringVC, isTransferringEE }) => {
  if (selectedParentEE.length > 0) return selectedParentEE[0];
  if (isZoneMode) return selectedParent?.position;
  if (isTransferringVC || isTransferringEE) return selectedParent?.id;
  return selectedParent?.ee_id || null;
};

const executeTransferAction = async ({ dispatch, transferConfig, onClose, onSuccess }) => {
  if (!transferConfig) {
    onClose();
    return;
  }
  try {
    const response = await Promise.resolve(
      dispatch(
        HandleApiActions({
          ...transferConfig.apiAction,
          params: transferConfig.payload,
          show_toast: true,
        })
      )
    );
    if (response && !response.error) {
      if (typeof onSuccess === "function") {
        onSuccess(transferConfig);
      }
      onClose();
    }
  } catch (err) {
    console.error("Error transferring item:", err);
  }
};

function useResetEETransferState({
  open,
  isTransferringEE,
  setEeTransferMode,
  setEeTransferModeSelection,
  setSelectedZone,
  setSelectedVS,
  setAssemblySearch,
  setPage,
  setSelectedParent,
  setSelectedParentEE,
}) {
  useEffect(() => {
    if (open && isTransferringEE) {
      setEeTransferMode(null);
      setEeTransferModeSelection("ZONE");
      setSelectedZone("");
      setSelectedVS("");
      setAssemblySearch("");
      setPage(1);
      setSelectedParent(null);
      setSelectedParentEE([]);
    }
  }, [open, isTransferringEE, setEeTransferMode, setEeTransferModeSelection, setSelectedZone, setSelectedVS, setAssemblySearch, setPage, setSelectedParent, setSelectedParentEE]);
}

function useDefaultZoneSelection({
  open,
  selectedZone,
  selectedProjectDetails,
  isTransferringVC,
  isTransferringSWM,
  eeTransferMode,
  setSelectedZone,
}) {
  useEffect(() => {
    const isZonal = Boolean(selectedProjectDetails?.is_zonal);
    const hasZones = selectedProjectDetails?.project_zones?.length > 0;
    const shouldSelect = isTransferringVC || isTransferringSWM || eeTransferMode === "VC";
    if (open && isZonal && !selectedZone && hasZones && shouldSelect) {
      setSelectedZone(selectedProjectDetails.project_zones[0].position);
    }
  }, [open, selectedZone, selectedProjectDetails, isTransferringVC, isTransferringSWM, eeTransferMode, setSelectedZone]);
}

const getTransferFlags = ({
  selectedArchivedEEs,
  selectedArchivedVCs,
  selectedArchivedVSs,
  selectedArchivedSWMs,
  eeTransferMode,
}) => {
  const isTransferringEE = Boolean(selectedArchivedEEs?.length);
  const isTransferringVC = Boolean(selectedArchivedVCs?.length);
  const isTransferringVS = Boolean(selectedArchivedVSs?.length);
  const isTransferringSWM = Boolean(selectedArchivedSWMs?.length);
  const isZoneMode = isTransferringVS || (isTransferringEE && eeTransferMode === "ZONE");
  const isVCMode = isTransferringVC || (isTransferringEE && eeTransferMode === "VC");
  return {
    isTransferringEE,
    isTransferringVC,
    isTransferringVS,
    isTransferringSWM,
    isZoneMode,
    isVCMode,
  };
};

function ChangeParentDialog({
  open,
  onClose,
  onSuccess,
  selectedArchivedEEs,
  selectedArchivedSWMs,
  selectedArchivedVSs,
  selectedArchivedVCs,
}) {
  const dispatch = useDispatch();
  const [assemblySearch, setAssemblySearch] = useState("");
  const [selectedParent, setSelectedParent] = useState(null);
  const [selectedParentEE, setSelectedParentEE] = useState([]);
  const [selectedZone, setSelectedZone] = useState("");
  const [eeTransferMode, setEeTransferMode] = useState(null);
  const [eeTransferModeSelection, setEeTransferModeSelection] = useState("ZONE");
  const selectedProjectDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedProjectDetails
  );
  const selectedVariantDetails = useSelector(
    (state) => state?.projectConfigReducer?.selectedVariantDetails
  );

  const [page, setPage] = useState(1);
  const rowsPerPage = 25;

  const {
    isTransferringEE,
    isTransferringVC,
    isTransferringVS,
    isTransferringSWM,
    isZoneMode,
    isVCMode,
  } = getTransferFlags({
    selectedArchivedEEs,
    selectedArchivedVCs,
    selectedArchivedVSs,
    selectedArchivedSWMs,
    eeTransferMode,
  });

  const { vehicleSystems, selectedVS, setSelectedVS } = useVehicleSystems({
    eeTransferMode,
    selectedZone,
    selectedProjectDetails,
    selectedVariantDetails,
    dispatch,
  });

  const { searchResults, totalItems } = useChangeParentSearch({
    open,
    selectedProjectDetails,
    selectedVariantDetails,
    isZoneMode,
    isTransferringVC,
    isTransferringEE,
    isTransferringSWM,
    eeTransferMode,
    selectedZone,
    selectedVS,
    page,
    rowsPerPage,
    assemblySearch,
    dispatch,
  });

  useResetEETransferState({
    open,
    isTransferringEE,
    setEeTransferMode,
    setEeTransferModeSelection,
    setSelectedZone,
    setSelectedVS,
    setAssemblySearch,
    setPage,
    setSelectedParent,
    setSelectedParentEE,
  });

  useEffect(() => {
    if (open && !selectedProjectDetails?.is_zonal) {
      setSelectedZone("");
    }
  }, [open, selectedProjectDetails?.is_zonal]);

  useDefaultZoneSelection({
    open,
    selectedZone,
    selectedProjectDetails,
    isTransferringVC,
    isTransferringSWM,
    eeTransferMode,
    setSelectedZone,
  });

  const handleConfirm = async () => {
    const targetParentId = getTargetParentIdHelper({
      selectedParentEE,
      isZoneMode,
      selectedParent,
      isTransferringVC,
      isTransferringEE,
    });

    console.log("---targetParentId---", targetParentId)
    const isTargetInvalid = (!targetParentId && !isZoneMode) || (isZoneMode && targetParentId == null);
    if (isTargetInvalid) {
      onClose();
      return;
    }

    const transferConfig = getTransferPayloadAndAction({
      isTransferringVS,
      isTransferringVC,
      isTransferringEE,
      targetParentId,
      selectedArchivedVSs,
      selectedArchivedVCs,
      selectedArchivedEEs,
      selectedArchivedSWMs,
      eeTransferMode,
      selectedParent,
      projectId: selectedProjectDetails?.project_id,
      variantId: selectedVariantDetails?.variant_id,
      isZonal: Boolean(selectedProjectDetails?.is_zonal),
      selectedZone,
      selectedProjectDetails,
    });
    console.log("---transferConfig---", transferConfig)
    await executeTransferAction({ dispatch, transferConfig, onClose, onSuccess });
  };

  const isInvalidVS = isTransferringVS && !selectedProjectDetails?.is_zonal;
  if (!open || isInvalidVS) return null;

  if (isTransferringEE && !eeTransferMode) {
    return (
      <TransferDestinationDialog
        open={open}
        onClose={onClose}
        value={eeTransferModeSelection}
        onChange={setEeTransferModeSelection}
        onNext={() => setEeTransferMode(eeTransferModeSelection)}
      />
    );
  }

  const showZoneSelect = (isVCMode || isTransferringSWM) && selectedProjectDetails?.is_zonal;
  const showVSSelect = isTransferringEE && eeTransferMode === "VC";
  const isConfirmDisabled = !selectedParent && selectedParentEE.length === 0;

  return (
    <CustomDialogBoxV2
      open={open}
      onClose={onClose}
      title="Select parent component"
      dialogRadius="0rem"
      requiresDefaultPaddingForContent={false}
      headerSlot={<></>}
      successButtonName="Confirm selection"
      rejectButtonName="Cancel"
      actionButtonStyle="continuous"
      onClickSuccess={handleConfirm}
      onClickReject={onClose}
      successButtonDisabled={isConfirmDisabled}
    >
      <Box sx={{ width: "600px", maxWidth: "100%" }}>
        <Card
          sx={{
            ...vlcComponentStyle.card,
            display: "flex",
            flexDirection: "column",
            height: "400px",
            boxShadow: "none",
            borderRadius: 0,
          }}
        >
          <CardHeader
            sx={vlcComponentStyle.cardHeader}
            title={
              <ChangeParentSearchHeader
                showZoneSelect={showZoneSelect}
                showVSSelect={showVSSelect}
                selectedProjectDetails={selectedProjectDetails}
                selectedZone={selectedZone}
                setSelectedZone={setSelectedZone}
                selectedVS={selectedVS}
                setSelectedVS={setSelectedVS}
                vehicleSystems={vehicleSystems}
                setPage={setPage}
                isTransferringVS={isTransferringVS}
                isTransferringVC={isTransferringVC}
                isTransferringEE={isTransferringEE}
                eeTransferMode={eeTransferMode}
                assemblySearch={assemblySearch}
                setAssemblySearch={setAssemblySearch}
              />
            }
          />
          <ChangeParentCardList
            searchResults={searchResults}
            assemblySearch={assemblySearch}
            isZoneMode={isZoneMode}
            isVCMode={isVCMode}
            isTransferringVC={isTransferringVC}
            isTransferringEE={isTransferringEE}
            eeTransferMode={eeTransferMode}
            selectedParent={selectedParent}
            selectedParentEE={selectedParentEE}
            onSelectParent={setSelectedParent}
            onSelectParentEE={setSelectedParentEE}
          />
          {totalItems > 0 && (
            <Box sx={{ mt: 2, borderTop: "1px solid #ddd", pt: 1 }}>
              <CustomPaginationV2
                page={page}
                rowsPerPage={rowsPerPage}
                onPageChange={(newPage) => setPage(newPage)}
                totalItems={totalItems}
              />
            </Box>
          )}
        </Card>
      </Box>
    </CustomDialogBoxV2>
  );
}

function ChangeParentCardList({
  searchResults,
  assemblySearch,
  isZoneMode,
  isVCMode,
  isTransferringVC,
  isTransferringEE,
  eeTransferMode,
  selectedParent,
  selectedParentEE,
  onSelectParent,
  onSelectParentEE,
}) {
  return (
    <CardContent
      sx={{
        flex: 1,
        overflowY: "auto",
        p: 2,
        maxHeight: "500px",
        padding: 0,
        margin: 0,
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {searchResults.length === 0 && assemblySearch && (
          <Typography variant="body2" color="textSecondary" align="center" mt={2}>
            No components found.
          </Typography>
        )}
        {searchResults.map((item) => (
          <ChangeParentItemCard
            key={getItemId(item, isZoneMode, isVCMode)}
            item={item}
            isZoneMode={isZoneMode}
            isVCMode={isVCMode}
            isTransferringVC={isTransferringVC}
            isTransferringEE={isTransferringEE}
            eeTransferMode={eeTransferMode}
            selectedParent={selectedParent}
            selectedParentEE={selectedParentEE}
            onSelectParent={onSelectParent}
            onSelectParentEE={onSelectParentEE}
          />
        ))}
      </Box>
    </CardContent>
  );
}

ChangeParentCardList.propTypes = {
  searchResults: PropTypes.array.isRequired,
  assemblySearch: PropTypes.string,
  isZoneMode: PropTypes.bool,
  isVCMode: PropTypes.bool,
  isTransferringVC: PropTypes.bool,
  isTransferringEE: PropTypes.bool,
  eeTransferMode: PropTypes.string,
  selectedParent: PropTypes.object,
  selectedParentEE: PropTypes.array,
  onSelectParent: PropTypes.func.isRequired,
  onSelectParentEE: PropTypes.func.isRequired,
};

ChangeParentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSuccess: PropTypes.func,
  selectedArchivedEEs: PropTypes.array,
  selectedArchivedSWMs: PropTypes.array,
  selectedArchivedVSs: PropTypes.array,
  selectedArchivedVCs: PropTypes.array,
};

export default ChangeParentDialog;
